import { Entitlement } from '@api/constants';
import { hasEntitlement } from '@api/utils';
import { useSession } from '@common/authorization/context/Session';
import { withRole } from '@common/authorization/Roles';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { LinkButton, NavigationBar, optionClasses } from '@imprivata-cloud/design-system';
import { ROLE_TYPE_ADMIN } from '@portal/constants';
import {
  useAgentVersionReport,
  useUserEnrollmentReport,
} from '@portal/containers/identityUsers/hooks/useReport';
import { SettingsPath } from '@portal/PortalRoutes';
import { downloadAsFileToUserComputer } from '@utils/utils';
import { message } from 'antd';
import { Download } from 'lucide-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import SuccessIcon from '../../../assets/icons/success.svg?react';
import { featureSupported } from '../../../featureFlagsUtils';

const ENABLE_LOGIN_ACTIVITY_REPORT = featureSupported('ENABLE_LOGIN_ACTIVITY_REPORT');
const ENABLE_EPCS = featureSupported('ENABLE_EPCS');
const ENABLE_ADVANCED_SETTINGS_TAB = featureSupported('ENABLE_ADVANCED_SETTINGS_TAB');

const SettingsNavigation = () => {
  const { t } = useTranslation();
  const navigateKeepSearch = useNavigateKeepSearch();
  const { emitError } = useErrorContext();
  const [messageApi, contextHolder] = message.useMessage();
  const { tenantInfo } = useSession();

  const enableEpcs = ENABLE_EPCS && hasEntitlement(tenantInfo, Entitlement.EPCS);
  const hasSsoEntitlement = hasEntitlement(tenantInfo, Entitlement.SSO);
  const enableApplications = hasSsoEntitlement;

  const CUSTOMIZE_LINKS = { className: optionClasses };

  const handleUsersClick = (path: string) => {
    navigateKeepSearch(path);
  };

  const { mutateAsync: getAgentVersionReport, isPending: isPendingAgentVersionReport } =
    useAgentVersionReport();
  const { mutateAsync: getUserEnrollmentReport, isPending: isPendingEnrollmentReport } =
    useUserEnrollmentReport();

  const handleDownloadReport = useCallback(
    async (fetchReport: () => Promise<Blob>, filename: string) => {
      try {
        const data = await fetchReport();
        if (!data) {
          return;
        }
        messageApi.success({
          duration: 10,
          className: 'download-started-banner',
          content: (
            <div className="banner-content">
              <div className="header">{t('portal.notifications.download.title')}</div>
              <div>{t('portal.notifications.download.text')}</div>
            </div>
          ),
          icon: <SuccessIcon />,
        });
        downloadAsFileToUserComputer(data, filename);
      } catch (e) {
        emitError(new AppError(e as ApiError));
      }
    },
    [emitError, messageApi, t],
  );

  const UsersPopover = () => (
    <div className="flex flex-col w-50">
      <LinkButton
        {...CUSTOMIZE_LINKS}
        aria-label={'entra-id-users'}
        onClick={() => handleUsersClick(SettingsPath.ENTRA_ID_USERS)}
      >
        {t('portal.settings.tabs-title.entra-id-users')}
      </LinkButton>
      <LinkButton
        {...CUSTOMIZE_LINKS}
        aria-label={'identity-users'}
        onClick={() => handleUsersClick(SettingsPath.IDENTITY_USERS)}
      >
        {t('portal.settings.tabs-title.identity-users')}
      </LinkButton>
    </div>
  );

  const IntegrationsPopover = () => (
    <div className="flex flex-col">
      <LinkButton
        {...CUSTOMIZE_LINKS}
        aria-label={'saml-idp'}
        onClick={() => handleUsersClick(SettingsPath.SAML_IDP)}
      >
        {t('portal.settings.tabs-title.saml-idp')}
      </LinkButton>
      <LinkButton
        {...CUSTOMIZE_LINKS}
        aria-label={'eam-integration'}
        onClick={() => handleUsersClick(SettingsPath.EAM_INTEGRATION)}
      >
        {t('portal.settings.tabs-title.eam-integration')}
      </LinkButton>
      <LinkButton
        {...CUSTOMIZE_LINKS}
        aria-label={'vpam-integration'}
        onClick={() => handleUsersClick(SettingsPath.VPAM_INTEGRATION)}
      >
        {t('portal.settings.tabs-title.vpam-integration')}
      </LinkButton>
      <LinkButton
        {...CUSTOMIZE_LINKS}
        aria-label={'aip-integration'}
        onClick={() => handleUsersClick(SettingsPath.AIP_INTEGRATION)}
      >
        {t('portal.settings.tabs-title.aip-integration')}
      </LinkButton>
    </div>
  );

  const ReportsPopover = () => (
    <div className="flex flex-col">
      {ENABLE_LOGIN_ACTIVITY_REPORT && (
        <LinkButton
          {...CUSTOMIZE_LINKS}
          aria-label={'login-activity-report'}
          onClick={() => handleUsersClick(SettingsPath.LOGIN_ACTIVITY_REPORT)}
        >
          {t('portal.settings.tabs-title.login-activity-report')}
        </LinkButton>
      )}
      {enableEpcs && (
        <LinkButton
          {...CUSTOMIZE_LINKS}
          aria-label={'epcs-activity-report'}
          onClick={() => handleUsersClick(SettingsPath.EPCS_ACTIVITY_REPORT)}
        >
          {t('portal.settings.tabs-title.epcs-activity-report')}
        </LinkButton>
      )}
      <hr className={'settings-divider'} />
      {hasSsoEntitlement && (
        <LinkButton
          {...CUSTOMIZE_LINKS}
          aria-label={'endpoint-data-report'}
          active={isPendingAgentVersionReport}
          onClick={() => handleDownloadReport(getAgentVersionReport, 'endpoint-data-report.csv')}
          icon={Download}
        >
          {t('portal.settings.tabs-title.endpoint-data-report')}
        </LinkButton>
      )}
      <LinkButton
        {...CUSTOMIZE_LINKS}
        aria-label={'user-data-report'}
        active={isPendingEnrollmentReport}
        onClick={() => handleDownloadReport(getUserEnrollmentReport, 'user-data-report.csv')}
        icon={Download}
      >
        {t('portal.settings.tabs-title.user-data-report')}
      </LinkButton>
    </div>
  );

  const SecurityPopover = () => (
    <div className="flex flex-col">
      <LinkButton
        {...CUSTOMIZE_LINKS}
        aria-label={'application-access'}
        onClick={() => handleUsersClick(SettingsPath.SECURITY)}
      >
        {t('portal.settings.tabs-title.application-access')}
      </LinkButton>
      <LinkButton
        {...CUSTOMIZE_LINKS}
        aria-label={'password-policy'}
        onClick={() => handleUsersClick(SettingsPath.PASSWORD_POLICY)}
      >
        {t('portal.settings.tabs-title.password-policy')}
      </LinkButton>
    </div>
  );

  return (
    <>
      {contextHolder}
      <NavigationBar>
        <NavigationBar.Start>
          <NavigationBar.Group>
            <NavigationBar.PopItem
              text={t('portal.settings.tabs-title.users')}
              aria-label={'users'}
            >
              <UsersPopover />
            </NavigationBar.PopItem>
            {enableApplications && (
              <NavigationBar.LinkItem
                onClick={() => handleUsersClick(SettingsPath.APPLICATIONS)}
                aria-label={'applications'}
              >
                {t('portal.settings.tabs-title.applications')}
              </NavigationBar.LinkItem>
            )}
            <NavigationBar.PopItem
              text={t('portal.settings.tabs-title.integrations')}
              aria-label={'integrations'}
            >
              <IntegrationsPopover />
            </NavigationBar.PopItem>
            <NavigationBar.PopItem
              text={t('portal.settings.tabs-title.reports')}
              aria-label={'reports'}
            >
              <ReportsPopover />
            </NavigationBar.PopItem>
            <NavigationBar.PopItem
              text={t('portal.settings.tabs-title.security')}
              aria-label={'security'}
            >
              <SecurityPopover />
            </NavigationBar.PopItem>
            <NavigationBar.LinkItem
              onClick={() => handleUsersClick(SettingsPath.CUSTOMIZE)}
              aria-label={'customize'}
            >
              {t('portal.settings.tabs-title.customize')}
            </NavigationBar.LinkItem>
            {ENABLE_ADVANCED_SETTINGS_TAB && hasSsoEntitlement && (
              <NavigationBar.LinkItem
                onClick={() => handleUsersClick(SettingsPath.ADVANCED_SETTINGS)}
                aria-label={'advanced-settings'}
              >
                {t('portal.settings.tabs-title.advanced-settings')}
              </NavigationBar.LinkItem>
            )}
          </NavigationBar.Group>
        </NavigationBar.Start>
      </NavigationBar>
    </>
  );
};

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(SettingsNavigation);
